import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

let __bp_arrow_id = 0;
const useIsoEffect =
  typeof window !== "undefined" ? useLayoutEffect : useEffect;

const clamp = (v, min = 0, max = 1) => Math.min(max, Math.max(min, v));

/**
 * Subtle scroll-progress arrow.
 *
 * The path is progressively drawn (stroke-dashoffset tied to scrollY)
 * as the user scrolls between `startRef` (current title) and `endRef`
 * (next title). Reverses naturally on scroll-up.
 *
 * Hidden on mobile (md+ only).
 */
export const ScrollArrow = ({
  d,
  startRef,
  endRef,
  className = "",
  viewBox = "0 0 280 480",
  strokeWidth = 1.5,
  opacity = 0.22,
  startOffset = 0.65, // start drawing when startEl is at 65% down viewport
  endOffset = 0.45, // finish drawing when endEl is at 45% down viewport
  testId,
}) => {
  const pathRef = useRef(null);
  const rafRef = useRef(null);
  const [markerVisible, setMarkerVisible] = useState(false);
  const idRef = useRef(`bp-arrow-${++__bp_arrow_id}`);
  const markerId = `${idRef.current}-marker`;

  // Initialize path length & dash properties.
  useIsoEffect(() => {
    const path = pathRef.current;
    if (!path) return;
    const len = path.getTotalLength();
    path.style.strokeDasharray = String(len);
    path.style.strokeDashoffset = String(len);
  }, [d]);

  // Bind scroll listener to drive draw progress.
  useEffect(() => {
    const path = pathRef.current;
    if (!path) return;
    const startEl = startRef?.current;
    const endEl = endRef?.current;
    if (!startEl || !endEl) return;

    let scrollA = 0;
    let scrollB = 1;
    let pathLen = path.getTotalLength();

    const measure = () => {
      pathLen = path.getTotalLength();
      const sRect = startEl.getBoundingClientRect();
      const eRect = endEl.getBoundingClientRect();
      const vh = window.innerHeight || 0;
      // Document-relative trigger Y values.
      scrollA = sRect.top + window.scrollY - vh * startOffset;
      scrollB = eRect.top + window.scrollY - vh * endOffset;
      if (scrollB - scrollA < 1) scrollB = scrollA + 1;
    };

    const update = () => {
      rafRef.current = null;
      const y = window.scrollY;
      const p = clamp((y - scrollA) / (scrollB - scrollA));
      path.style.strokeDashoffset = String(pathLen * (1 - p));
      // Show arrow marker once nearly drawn (avoids floating arrowhead at start).
      setMarkerVisible(p >= 0.96);
    };

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(update);
    };

    const onResize = () => {
      measure();
      onScroll();
    };

    measure();
    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);

    // Recompute after fonts/images settle.
    const settleTimer = window.setTimeout(onResize, 400);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.clearTimeout(settleTimer);
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    };
  }, [startRef, endRef, startOffset, endOffset]);

  return (
    <svg
      viewBox={viewBox}
      className={`pointer-events-none select-none hidden md:block ${className}`}
      style={{ opacity, willChange: "auto" }}
      aria-hidden="true"
      data-testid={testId}
      preserveAspectRatio="none"
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L6,3 L0,6 z" fill="#0A2540" />
        </marker>
      </defs>
      <path
        ref={pathRef}
        d={d}
        fill="none"
        stroke="#0A2540"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        markerEnd={markerVisible ? `url(#${markerId})` : undefined}
        style={{ transition: "stroke-dashoffset 80ms linear" }}
      />
    </svg>
  );
};

export default ScrollArrow;
